import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { Alert, Button, Switch, Typography } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import {
  SettingRow,
  SettingRows,
  SettingsCard,
} from "components/platform/accounts/SettingsCard";
import { GET_AVAILABLE_PLANS } from "graphql/platform";
import { Checkbox, Drawer, FormField } from "@outpave/ds";

type PlanOption = {
  id: string;
  name: string | null;
  description: string | null;
  /**
   * App identity keys this plan declares (feature_app_spend, feature_app_byoc, ...).
   * On an app plan this is the app's own identity. On an add-on it is the set of apps
   * the add-on belongs to; empty means the add-on is universal.
   */
  appFeatureKeys: string[];
};

type AvailablePlansData = {
  availablePlans: {
    appPlans: PlanOption[];
    addOnPlans: PlanOption[];
  };
};

interface PlanPickerDrawerProps {
  open: boolean;
  onClose: () => void;
  appPlanIds: string[];
  addOnPlanIds: string[];
  onSave: (next: { appPlanIds: string[]; addOnPlanIds: string[] }) => void;
  companyName?: string | null;
}

/** Mirrors addOnIsAllowed on the server: universal add-ons always pass. */
const addOnIsAllowed = (plan: PlanOption, allowed: Set<string>) =>
  plan.appFeatureKeys.length === 0 ||
  plan.appFeatureKeys.some((key) => allowed.has(key));

const cleanAppName = (name: string | null | undefined, fallback: string) =>
  (name || fallback).replace(/^application\s*-\s*/i, "");

const collectAppFeatureKeys = (
  planIds: string[],
  planById: Map<string, PlanOption>,
) => {
  const keys = new Set<string>();
  planIds.forEach((id) =>
    planById.get(id)?.appFeatureKeys.forEach((key) => keys.add(key)),
  );
  return keys;
};

export interface PlanChange {
  appPlanIds: string[];
  addOnPlanIds: string[];
  /** Add-ons the change dropped because the app they belong to went. */
  cascadedAddOnIds: string[];
}

/**
 * A company's plan choice being edited: the catalogue, the picks, and what the
 * picks allow. `active` gates the catalogue query and resets the picks to the
 * saved plans whenever it turns on or they change.
 */
export const usePlanSelection = (
  appPlanIds: string[],
  addOnPlanIds: string[],
  active: boolean,
  onChange?: (change: PlanChange) => void,
) => {
  const { t } = useTranslation();

  const { data, loading, error } = useQuery<AvailablePlansData>(
    GET_AVAILABLE_PLANS,
    {
      fetchPolicy: "cache-and-network",
      skip: !active,
    },
  );

  const appPlans = useMemo(() => data?.availablePlans?.appPlans || [], [data]);
  const addOnPlans = useMemo(
    () => data?.availablePlans?.addOnPlans || [],
    [data],
  );

  const [localAppPlanIds, setLocalAppPlanIds] = useState<string[]>(appPlanIds);
  const [localAddOnPlanIds, setLocalAddOnPlanIds] =
    useState<string[]>(addOnPlanIds);

  useEffect(() => {
    if (active) {
      setLocalAppPlanIds(appPlanIds);
      setLocalAddOnPlanIds(addOnPlanIds);
    }
  }, [active, appPlanIds, addOnPlanIds]);

  const planById = useMemo(
    () =>
      new Map<string, PlanOption>(
        [...appPlans, ...addOnPlans].map((p) => [p.id, p]),
      ),
    [appPlans, addOnPlans],
  );

  const allowedAppFeatureKeys = useMemo(
    () => collectAppFeatureKeys(localAppPlanIds, planById),
    [localAppPlanIds, planById],
  );

  /** App identity key -> that app's display name, for the "Requires X" label. */
  const appNameByFeatureKey = useMemo(() => {
    const map = new Map<string, string>();
    appPlans.forEach((p) =>
      p.appFeatureKeys.forEach((key) =>
        map.set(key, cleanAppName(p.name, p.id)),
      ),
    );
    return map;
  }, [appPlans]);

  /**
   * Every add-on, blocked ones included, with the apps that would unlock them.
   * Blocked add-ons are shown rather than hidden so the owner can see what exists
   * and what it costs; sorting the selectable ones first keeps the actionable
   * choices at the top. Sort is stable, so catalogue order is otherwise preserved.
   */
  const decoratedAddOns = useMemo(() => {
    const decorated = addOnPlans.map((plan) => ({
      plan,
      allowed: addOnIsAllowed(plan, allowedAppFeatureKeys),
      requiredAppNames: plan.appFeatureKeys
        .map((key) => appNameByFeatureKey.get(key))
        .filter((name): name is string => Boolean(name)),
    }));
    return decorated.sort((a, b) => Number(b.allowed) - Number(a.allowed));
  }, [addOnPlans, allowedAppFeatureKeys, appNameByFeatureKey]);

  /**
   * Add-ons already on the company whose app plan is not selected. Only reachable for
   * companies whose plans predate app tagging, since the cascade below prevents new
   * orphans. Surfaced as a warning rather than auto-cleared: the server rejects them on
   * save, so the owner needs to see why.
   */
  const orphanedSelection = useMemo(
    () =>
      localAddOnPlanIds.filter((id) => {
        const plan = planById.get(id);
        if (!plan) return false;
        return !addOnIsAllowed(plan, allowedAppFeatureKeys);
      }),
    [localAddOnPlanIds, planById, allowedAppFeatureKeys],
  );

  /**
   * Deselecting an app drops the add-ons that belonged only to it.
   *
   * This lives in the change handler rather than an effect on allowedAppFeatureKeys
   * because an effect would also run on mount and on every query refetch, silently
   * clearing a legacy company's pre-existing orphans before the owner has touched
   * anything. Confining it here keeps the cascade a consequence of an explicit action.
   */
  const commit = (
    nextAppPlanIds: string[],
    nextAddOnPlanIds: string[],
    cascadedAddOnIds: string[] = [],
  ) => {
    setLocalAppPlanIds(nextAppPlanIds);
    setLocalAddOnPlanIds(nextAddOnPlanIds);
    onChange?.({
      appPlanIds: nextAppPlanIds,
      addOnPlanIds: nextAddOnPlanIds,
      cascadedAddOnIds,
    });
  };

  const handleAppChange = (nextAppPlanIds: string[]) => {
    const allowed = collectAppFeatureKeys(nextAppPlanIds, planById);
    const nextAddOnPlanIds = localAddOnPlanIds.filter((id) => {
      const plan = planById.get(id);
      if (!plan) return true; // unknown plan: never strip silently
      // One already orphaned stays for the owner to remove; only an add-on this change orphans goes with it.
      return (
        addOnIsAllowed(plan, allowed) ||
        !addOnIsAllowed(plan, allowedAppFeatureKeys)
      );
    });
    commit(
      nextAppPlanIds,
      nextAddOnPlanIds,
      localAddOnPlanIds.filter((id) => !nextAddOnPlanIds.includes(id)),
    );
  };

  // Split rather than joining on a translated conjunction: " or " is a poor i18n key,
  // and word order around a list varies by language.
  const requiresLabel = (appNames: string[]) => {
    if (appNames.length === 1)
      return t("Requires {{app}}", { app: appNames[0] });
    if (appNames.length > 1)
      return t("Requires one of {{apps}}", { apps: appNames.join(", ") });
    return t("Requires an app that is not selected");
  };

  const toggleApp = (id: string) =>
    handleAppChange(
      localAppPlanIds.includes(id)
        ? localAppPlanIds.filter((x) => x !== id)
        : [...localAppPlanIds, id],
    );

  const toggleAddOn = (id: string) =>
    commit(
      localAppPlanIds,
      localAddOnPlanIds.includes(id)
        ? localAddOnPlanIds.filter((x) => x !== id)
        : [...localAddOnPlanIds, id],
    );

  const reset = () => {
    setLocalAppPlanIds(appPlanIds);
    setLocalAddOnPlanIds(addOnPlanIds);
  };

  const planName = (id: string) => {
    const plan = planById.get(id);
    return plan ? plan.name || plan.id : id;
  };

  return {
    loading,
    error,
    appPlans,
    addOnPlans,
    localAppPlanIds,
    localAddOnPlanIds,
    decoratedAddOns,
    orphanedSelection,
    requiresLabel,
    toggleApp,
    toggleAddOn,
    reset,
    planName,
    canSave: localAppPlanIds.length > 0 && orphanedSelection.length === 0,
  };
};

export type PlanSelection = ReturnType<typeof usePlanSelection>;

/**
 * The apps and add-ons as settings rows. `live` saves each switch as it flips, on
 * the account's Plans tab; otherwise the rows are checkboxes the surrounding form
 * saves, as the new-account drawer does.
 */
export const PlanPickerFields: React.FC<{
  selection: PlanSelection;
  live?: boolean;
  disabled?: boolean;
}> = ({ selection, live = false, disabled = false }) => {
  const { t } = useTranslation();
  const {
    loading,
    error,
    appPlans,
    addOnPlans,
    localAppPlanIds,
    localAddOnPlanIds,
    decoratedAddOns,
    orphanedSelection,
    requiresLabel,
    toggleApp,
    toggleAddOn,
  } = selection;

  // A catalogue that failed to load would otherwise read as one with nothing to offer.
  if (error && !appPlans.length && !addOnPlans.length) {
    return (
      <Alert severity="error" role="status">
        {t("PlanCatalogueLoadError")}
      </Alert>
    );
  }

  const control =
    (checked: boolean, locked: boolean, onToggle: () => void) =>
    (labelId: string) =>
      live ? (
        <Switch
          checked={checked}
          disabled={disabled || locked}
          onChange={onToggle}
          slotProps={{ input: { "aria-labelledby": labelId } }}
        />
      ) : (
        <Checkbox
          checked={checked}
          disabled={disabled || locked}
          onChange={onToggle}
          slotProps={{ input: { "aria-labelledby": labelId } }}
        />
      );

  const apps =
    appPlans.length === 0 && !loading ? (
      <Typography variant="body2">{t("No apps available")}</Typography>
    ) : (
      <SettingRows>
        {appPlans.map((plan, index) => {
          const checked = localAppPlanIds.includes(plan.id);
          return (
            <SettingRow
              key={plan.id}
              title={cleanAppName(plan.name, plan.id)}
              description={plan.description}
              divider={index < appPlans.length - 1}
              // Every company keeps at least one app.
              control={control(
                checked,
                checked && localAppPlanIds.length === 1,
                () => toggleApp(plan.id),
              )}
            />
          );
        })}
      </SettingRows>
    );

  const addOns =
    addOnPlans.length === 0 && !loading ? (
      <Typography variant="body2">{t("No add-on plans available")}</Typography>
    ) : (
      <SettingRows>
        {decoratedAddOns.map(({ plan, allowed, requiredAppNames }, index) => {
          const checked = localAddOnPlanIds.includes(plan.id);
          return (
            <SettingRow
              key={plan.id}
              title={plan.name || plan.id}
              // A blocked add-on says what unlocks it rather than what it does.
              description={
                allowed ? plan.description : requiresLabel(requiredAppNames)
              }
              divider={index < decoratedAddOns.length - 1}
              // A blocked add-on that is already on stays switchable, so a
              // pre-existing orphan can still be removed.
              control={control(checked, !allowed && !checked, () =>
                toggleAddOn(plan.id),
              )}
            />
          );
        })}
      </SettingRows>
    );

  const warning = orphanedSelection.length > 0 && (
    <Alert severity="warning" role="status">
      {t("OrphanedAddOnsWarning")}
    </Alert>
  );

  if (live) {
    return (
      <Stack gap="var(--space-5)">
        {warning}
        <SettingsCard title={t("Apps")}>{apps}</SettingsCard>
        <SettingsCard title={t("Add-ons")}>{addOns}</SettingsCard>
      </Stack>
    );
  }

  return (
    <Stack gap="var(--space-5)">
      {warning}
      <FormField asFieldset fullWidth label={t("Apps")}>
        {apps}
      </FormField>
      <FormField asFieldset fullWidth label={t("Add-ons")}>
        {addOns}
      </FormField>
    </Stack>
  );
};

const PlanPickerDrawer: React.FC<PlanPickerDrawerProps> = ({
  open,
  onClose,
  appPlanIds,
  addOnPlanIds,
  onSave,
  companyName,
}) => {
  const { t } = useTranslation();
  const selection = usePlanSelection(appPlanIds, addOnPlanIds, open);

  const handleSave = () => {
    onSave({
      appPlanIds: selection.localAppPlanIds,
      addOnPlanIds: selection.localAddOnPlanIds,
    });
    onClose();
  };

  // Gates the overlay AND every dismissal path, so the two can't drift.
  const busy = selection.loading && selection.appPlans.length === 0;

  const title: React.ReactNode | React.ReactNode[] = companyName
    ? [t("Plans"), companyName]
    : t("Plans");

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      title={title}
      loading={busy}
      footer={
        <>
          <Button variant="default-outlined" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!selection.canSave}>
            {t("Save")}
          </Button>
        </>
      }
    >
      <PlanPickerFields selection={selection} />
    </Drawer>
  );
};

export default PlanPickerDrawer;
